import React, { useEffect } from 'react';
import { Fx, Group, K, O, V } from '../../pages/PromptDetail/Formula';
import '../../pages/PromptDetail/PromptDetail.css';
import '../ScoreBadge/ScoreBadge.css';
import './ScoringCriteriaModal.css';
import { WorkedExample } from './WorkedExample';

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

interface ScoringCriteriaModalProps {
  open: boolean;
  onClose: () => void;
}

function Res({ tone, title, detail }: { tone: string; title: string; detail?: string }) {
  return (
    <span className={`sc-res sc-res--${tone}`}>
      {title}
      {detail ? <small>{detail}</small> : null}
    </span>
  );
}

function Plain({ title, detail, bad }: { title: string; detail?: string; bad?: boolean }) {
  return (
    <span className={`sc-plain${bad ? ' sc-plain--bad' : ''}`}>
      {title}
      {detail ? <small>{detail}</small> : null}
    </span>
  );
}

function Tier({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={`sc-pill sc-pill--${tone}`}>
      <i aria-hidden />
      {children}
    </span>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span className="sc-op sc-op--paren">(</span>
      {children}
      <span className="sc-op sc-op--paren">)</span>
    </>
  );
}

export const ScoringCriteriaModal: React.FC<ScoringCriteriaModalProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="scoring-modal-overlay" onClick={onClose}>
      <div
        className="scoring-modal scoring-modal--v4"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="scoring-criteria-title"
      >
        <div className="scoring-modal__header">
          <div>
            <h2 id="scoring-criteria-title" className="scoring-modal__title">Prompt Scoring Criteria</h2>
            <p className="scoring-modal__lead">
              Every prompt gets a score out of 10. It is built from a few smaller scores. Each one is highlighted below so you can see where your points came from.
            </p>
          </div>
          <button className="scoring-modal__close-btn scoring-modal__close-btn--top" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="scoring-modal__scroll">
              <section className="sc-sect">
                <h3>The big picture</h3>
                <p className="sc-lead">
                  Your 10 points are a base score plus a skill bonus. Rules and agents can add more. Penalties take points back. The total never goes above 10.
                </p>
                <div>
                  <div className="sc-bar" role="img" aria-label="Base score up to 8, skill credit up to 2">
                    <div className="sc-bar__base">Base score · up to 8</div>
                    <div className="sc-bar__skill">Skill credit · up to 2</div>
                  </div>
                  <div className="sc-legend">
                    <span><i className="sc-swatch sc-swatch--bonus" aria-hidden />Supporting rule or agent: +0.5 each</span>
                    <span><i className="sc-swatch sc-swatch--pen" aria-hidden />Penalties: −0.5 or −1 each</span>
                  </div>
                </div>
                <div className="sc-eq">
                  <Term><Res tone="base" title="Base score" detail="0 to 8" /></Term>
                  <span className="sc-op">+</span>
                  <Term><Res tone="credit" title="Skill credit" detail="0 to 2" /></Term>
                  <span className="sc-op">+</span>
                  <Term><Res tone="bonus" title="Supporting bonus" detail="+0.5 per rule or agent" /></Term>
                  <span className="sc-op">−</span>
                  <Term><Res tone="pen" title="Penalties" detail="all that apply" /></Term>
                  <span className="sc-op">=</span>
                  <Term><Res tone="overall" title="Overall score" detail="capped at 10" /></Term>
                </div>
              </section>

              <section className="sc-sect">
                <h3>How each score is built</h3>
                <p className="sc-lead">
                  Left: what goes in. Right: the highlighted score it produces. Type score and relevance score are scores in their own right, so they are highlighted too.
                </p>
                <div className="sc-flow">
                  <div className="sc-flow__in">
                    <Plain title="Task clarity" detail="0 to 10 · is the ask clear?" />
                    <span className="sc-op sc-op--sm">+</span>
                    <Plain title="Prompt context" detail="0 to 10 · did you point at the work?" />
                  </div>
                  <span className="sc-flow__ar" aria-hidden />
                  <div className="sc-flow__out">
                    <Res tone="base" title="Base score · 0 to 8" />
                    <span className="sc-flow__how">
                      <Fx>
                        <Group>
                          <K>0.4</K>
                          <O>×</O>
                          <V>clarity</V>
                        </Group>
                        <O>+</O>
                        <Group>
                          <K>0.4</K>
                          <O>×</O>
                          <V>context</V>
                        </Group>
                      </Fx>
                    </span>
                  </div>

                  <div className="sc-flow__in">
                    <Res tone="type" title="Type score" detail="0 to 1 · approved in HUB?" />
                    <span className="sc-op sc-op--sm">×</span>
                    <Res tone="rel" title="Relevance score" detail="0 to 1 · fits this task?" />
                    <span className="sc-op sc-op--sm">× 2</span>
                  </div>
                  <span className="sc-flow__ar" aria-hidden />
                  <div className="sc-flow__out">
                    <Res tone="credit" title="Skill credit · 0 to 2" />
                    <span className="sc-flow__how">
                      <Fx>
                        <Group>
                          <V tone="type">type</V>
                          <O>×</O>
                          <V tone="rel">relevance</V>
                          <O>×</O>
                          <K>2</K>
                        </Group>
                      </Fx>
                    </span>
                  </div>

                  <div className="sc-flow__in">
                    <Plain title="Supporting Rule" detail="attached? +0.5" />
                    <Plain title="Supporting Agent" detail="attached? +0.5" />
                  </div>
                  <span className="sc-flow__ar" aria-hidden />
                  <div className="sc-flow__out">
                    <Res tone="bonus" title="Supporting bonus · 0, 0.5 or 1" />
                    <span className="sc-flow__how">0.5 for each one attached</span>
                  </div>

                  <div className="sc-flow__in sc-flow__in--groups">
                    <div className="sc-flow__group">
                      <span className="sc-sub">Attachment-based penalties</span>
                      <Plain title="Rejected attachment" />
                      <Plain title="Unidentified skill" />
                    </div>
                    <div className="sc-flow__group">
                      <span className="sc-sub">Task intent penalties</span>
                      <Plain title="Unplanned rework" />
                      <Plain title="Wrong interpretation" />
                    </div>
                  </div>
                  <span className="sc-flow__ar" aria-hidden />
                  <div className="sc-flow__out">
                    <Res tone="pen" title="Penalties · total taken off" />
                    <span className="sc-flow__how">add up every one that applies</span>
                  </div>
                </div>
              </section>

              <section className="sc-sect">
                <h3>What each score rewards, and what it costs</h3>
                <div className="sc-cols">
                  <div className="sc-col">
                    <h4>Type score</h4>
                    <p className="sc-rule">Is your skill approved in HUB? Averaged over your skills.</p>
                    <div className="sc-row"><Tier tone="org">Org</Tier><em>1</em></div>
                    <div className="sc-row"><Tier tone="dom">Domain</Tier><em>0.5</em></div>
                    <div className="sc-row"><Tier tone="exp">Experimental</Tier><em>0.25</em></div>
                    <div className="sc-row"><Tier tone="rej">Rejected</Tier><em>no credit · −0.5</em></div>
                    <div className="sc-row"><Tier tone="unk">Unidentified</Tier><em>no credit · −0.5</em></div>
                  </div>
                  <div className="sc-col">
                    <h4>Relevance score</h4>
                    <p className="sc-rule">Does the skill fit this task? Only skills identified in HUB are counted.</p>
                    <div className="sc-row"><Tier tone="core">Core</Tier><em>1</em></div>
                    <div className="sc-row"><Tier tone="sup">Supporting</Tier><em>0.5</em></div>
                    <div className="sc-row"><Tier tone="irr">Irrelevant</Tier><em>0</em></div>
                    <h4 className="sc-col__next">Supporting bonus</h4>
                    <p className="sc-rule">Rules and agents you attach.</p>
                    <div className="sc-bon"><span>Supporting Rule</span><b>+0.5</b></div>
                    <div className="sc-bon"><span>Supporting Agent</span><b>+0.5</b></div>
                  </div>
                  <div className="sc-col">
                    <h4>Penalties</h4>
                    <div className="sc-pen-block">
                      <h5>Attachment-based penalties</h5>
                      <div className="sc-pen"><span>Rejected attachment<small>A skill or rule that HUB rejected</small></span><b>−0.5</b></div>
                      <div className="sc-pen"><span>Unidentified skill<small>A skill HUB cannot find</small></span><b>−0.5</b></div>
                    </div>
                    <div className="sc-pen-block">
                      <h5>Task intent penalties</h5>
                      <div className="sc-pen"><span>Unplanned rework<small>Session intent: you had to redo finished work</small></span><b>−1</b></div>
                      <div className="sc-pen"><span>Wrong interpretation<small>Session intent: the AI misread the ask</small></span><b>−1</b></div>
                    </div>
                    <p className="sc-rule">Each one counts every time it happens. A rule file with no tier is not penalized.</p>
                  </div>
                </div>
              </section>

              <section className="sc-sect">
                <h3>How to read your score</h3>
                <div className="sc-tiers">
                  <span className="score-badge score-badge--high">7 to 10 · Strong</span>
                  <span className="score-badge score-badge--medium">5 to 7 · Fair</span>
                  <span className="score-badge score-badge--low">Below 5 · Needs work</span>
                </div>
                <div className="sc-tips">
                  <div className="sc-tip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" aria-hidden><path d="M4 6h16M4 12h10M4 18h6" /></svg>
                    <div><b>State the outcome first</b><span>Say what finished looks like, then the rules it must follow.</span></div>
                  </div>
                  <div className="sc-tip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" aria-hidden><path d="M3 12h4M17 12h4M12 3v4M12 17v4" /><circle cx="12" cy="12" r="3" /></svg>
                    <div><b>Point at the files</b><span>Name the file or function you want changed.</span></div>
                  </div>
                  <div className="sc-tip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" aria-hidden><path d="M20 6L9 17l-5-5" /></svg>
                    <div><b>Attach skills that fit</b><span>Choose approved HUB skills that match the task, and skip the rest.</span></div>
                  </div>
                  <div className="sc-tip">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" aria-hidden><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /></svg>
                    <div><b>Check names before sending</b><span>A skill HUB cannot find or has rejected costs 0.5 and adds nothing.</span></div>
                  </div>
                </div>
              </section>

              <WorkedExample />
        </div>

        <div className="scoring-modal__footer">
          <button className="scoring-modal__close-btn scoring-modal__close-btn--bottom scoring-modal__close-btn--primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoringCriteriaModal;
